/**
 * ConnectFourGame - Main container component for the Connect Four game
 *
 * Orchestrates game mode selection, engine initialization, AI integration,
 * local multiplayer, online multiplayer via MultiplayerConnectFourService,
 * persistence, statistics, and error handling.
 *
 * Uses UniversalGameLayout for consistent layout across all games.
 *
 * Requirements: 7.2, 11.1–11.5, 12.5, 16.5, 18.5, 20.1, 20.4–20.7
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ConnectFourEngine } from '../ConnectFourEngine';
import { ConnectFourAI } from '../ConnectFourAI';
import { ConnectFourBoard } from './ConnectFourBoard';
import { MoveHistory } from './MoveHistory';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { GameCoachService } from '../../services/GameCoachService';
import { connectFourKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { MultiplayerConnectFourService } from '../services/MultiplayerConnectFourService';
import { WebSocketService } from '../../services/WebSocketService';
import { NotificationService } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { useAuthentication } from '../../hooks/useAuthentication';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import {
  ConnectFourGameSettings,
  ConnectFourDifficulty,
  ConnectFourMove,
  DiscColor,
} from '../types';
import './ConnectFourGame.css';

export interface ConnectFourGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

type GameMode = 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';

const ConnectFourGameInner: React.FC<ConnectFourGameProps> = ({ onExit, initialRoomCode }) => {
  const { isAuthenticated } = useAuthentication();

  // Core game state
  const [engine, setEngine] = useState<ConnectFourEngine>(() => new ConnectFourEngine());
  const [gameMode, setGameMode] = useState<GameMode | null>(null);
  const [aiDifficulty, setAiDifficulty] = useState<ConnectFourDifficulty>('medium');
  const [ai] = useState(() => new ConnectFourAI('medium'));
  const [moveCount, setMoveCount] = useState(0);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [aiMoveDelay] = useState(500);

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerConnectFourService>(
    () => new MultiplayerConnectFourService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [playerColor, setPlayerColor] = useState<DiscColor | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  // Refs
  const aiMoveScheduled = useRef(false);
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);
  const gameSessionId = useRef<string | null>(null);
  const coachRef = useRef(new GameCoachService('Connect Four', connectFourKnowledge));

  // -----------------------------------------------------------------------
  // Multiplayer event handlers
  // -----------------------------------------------------------------------
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
      const newEngine = new ConnectFourEngine();
      newEngine.initializeGame();
      setEngine(newEngine);
      setMoveCount((prev) => prev + 1);
    });

    multiplayerService.onMoveUpdate((move: ConnectFourMove) => {
      if (move && move.column !== undefined) {
        engine.dropDisc(move.column);
        setMoveCount((prev) => prev + 1);
      }
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const myColor = multiplayerService.getPlayerColor();
      let message = '';

      if (result.result === 'win') {
        message = result.winner === myColor ? '🎉 You win!' : '😔 You lost!';
      } else if (result.result === 'draw') {
        message = '🤝 Game ended in a draw!';
      } else if (result.result === 'resignation') {
        message = result.winner === myColor
          ? '🎉 Opponent resigned. You win!'
          : '😔 You resigned.';
      }

      NotificationService.getInstance().sendInfoNotification(message);
      setMoveCount((prev) => prev + 1);
    });

    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) {
        engine.setGameState(gameState);
        setMoveCount((prev) => prev + 1);
      }
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    multiplayerService.onReconnectionFailed(() => {
      NotificationService.getInstance().sendErrorNotification(
        'Unable to reconnect. Please return to the menu and try again.'
      );
    });
  }, [multiplayerService, engine]);

  // -----------------------------------------------------------------------
  // Deep link room code handling
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;

      setGameMode('multiplayer-realtime');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);
      setShowModeSelector(false);

      multiplayerService
        .joinRoom(initialRoomCode)
        .then(() => {
          const color = multiplayerService.getPlayerColor();
          if (color) setPlayerColor(color);
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setShowModeSelector(true);
          setWaitingForOpponent(false);
          setRoomCode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Game mode handlers
  // -----------------------------------------------------------------------

  const handleStartGame = useCallback(
    async (settings: ConnectFourGameSettings) => {
      if (settings.mode === 'multiplayer-realtime' && settings.isPrivate) {
        if (isCreatingRoom.current) return;
        isCreatingRoom.current = true;

        try {
          const room = await multiplayerService.createRoom();
          setRoomCode(room.code);
          setWaitingForOpponent(true);
          const color = multiplayerService.getPlayerColor();
          if (color) setPlayerColor(color);
          setGameMode('multiplayer-realtime');
          setShowModeSelector(false);
          isCreatingRoom.current = false;
        } catch (error: any) {
          NotificationService.getInstance().sendErrorNotification(
            `Failed to create room: ${error.message || 'Unknown error'}`
          );
          isCreatingRoom.current = false;
        }
        return;
      }

      const newEngine = new ConnectFourEngine();
      newEngine.initializeGame();
      setEngine(newEngine);

      setGameMode(settings.mode);
      setShowModeSelector(false);
      setMoveCount(0);
      setIsAIThinking(false);
      aiMoveScheduled.current = false;

      try {
        const sessionId = GameSessionManager.getInstance().startGameSession('connect-four');
        gameSessionId.current = sessionId;
      } catch (e) {
        // Non-critical
      }

      if (settings.mode === 'singleplayer' && settings.aiDifficulty) {
        setAiDifficulty(settings.aiDifficulty);
        ai.setDifficulty(settings.aiDifficulty);
      }
    },
    [ai, multiplayerService]
  );

  const handleJoinPrivateGame = useCallback(
    async (code: string) => {
      setGameMode('multiplayer-realtime');
      setRoomCode(code);
      setWaitingForOpponent(true);
      setShowModeSelector(false);

      try {
        await multiplayerService.joinRoom(code);
        const color = multiplayerService.getPlayerColor();
        if (color) setPlayerColor(color);
        setWaitingForOpponent(false);
        setOpponentConnected(true);
      } catch (error: any) {
        NotificationService.getInstance().sendErrorNotification(
          `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
        );
        setShowModeSelector(true);
        setWaitingForOpponent(false);
        setRoomCode(null);
        setGameMode(null);
      }
    },
    [multiplayerService]
  );

  // -----------------------------------------------------------------------
  // AI move logic
  // -----------------------------------------------------------------------

  const makeAIMove = useCallback(() => {
    if (engine.isGameOver()) {
      aiMoveScheduled.current = false;
      return;
    }

    setIsAIThinking(true);

    setTimeout(() => {
      try {
        const timeLimit =
          aiDifficulty === 'easy' ? 500 : aiDifficulty === 'medium' ? 1000 : 2000;
        const bestCol = ai.calculateBestMove(engine, timeLimit);

        if (bestCol !== null) {
          engine.dropDisc(bestCol);
        }
      } catch (error) {
        console.error('ConnectFourGame: Error during AI move:', error);
      } finally {
        setIsAIThinking(false);
        aiMoveScheduled.current = false;
        setMoveCount((prev) => prev + 1);
      }
    }, aiMoveDelay);
  }, [engine, ai, aiDifficulty, aiMoveDelay]);

  // -----------------------------------------------------------------------
  // Move handler
  // -----------------------------------------------------------------------

  const handleMove = useCallback(
    (move: ConnectFourMove) => {
      setMoveCount((prev) => prev + 1);

      if (engine.isGameOver() && gameSessionId.current) {
        try {
          GameSessionManager.getInstance().endGameSession(gameSessionId.current, true);
          gameSessionId.current = null;
        } catch (e) {
          // Non-critical
        }
      }

      if (gameMode === 'multiplayer-realtime') {
        multiplayerService.sendMove(move);
      }

      if (gameMode === 'singleplayer' && !engine.isGameOver()) {
        if (engine.getCurrentPlayer() === 'yellow' && !aiMoveScheduled.current) {
          aiMoveScheduled.current = true;
          setTimeout(() => makeAIMove(), 100);
        }
      }
    },
    [gameMode, engine, makeAIMove, multiplayerService]
  );

  // -----------------------------------------------------------------------
  // Undo handler (single-player only)
  // -----------------------------------------------------------------------

  const handleUndo = useCallback(() => {
    if (gameMode !== 'singleplayer') return;
    if (isAIThinking) return;

    const success = engine.undoMove(2);
    if (success) {
      setMoveCount((prev) => prev + 1);
    }
  }, [engine, gameMode, isAIThinking]);

  // -----------------------------------------------------------------------
  // New game / exit
  // -----------------------------------------------------------------------

  const handleNewGame = useCallback(() => {
    if (roomCode) {
      multiplayerService.leaveGame();
    }

    const newEngine = new ConnectFourEngine();
    newEngine.initializeGame();
    setEngine(newEngine);
    setGameMode(null);
    setShowModeSelector(true);
    setMoveCount(0);
    setIsAIThinking(false);
    aiMoveScheduled.current = false;

    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setPlayerColor(null);
    setConnectionStatus('connected');
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  // -----------------------------------------------------------------------
  // Multiplayer actions
  // -----------------------------------------------------------------------

  const handleResign = useCallback(() => {
    if (gameMode !== 'multiplayer-realtime') return;
    multiplayerService.resign();
    NotificationService.getInstance().sendInfoNotification('You resigned.');
  }, [gameMode, multiplayerService]);

  const handleCancelWaiting = useCallback(() => {
    multiplayerService.leaveGame();
    setWaitingForOpponent(false);
    setRoomCode(null);
    setShowModeSelector(true);
    setGameMode(null);
    setPlayerColor(null);
    isCreatingRoom.current = false;
  }, [multiplayerService]);

  // -----------------------------------------------------------------------
  // Display helpers
  // -----------------------------------------------------------------------

  const getTurnIndicatorText = (): string => {
    const currentPlayer = engine.getCurrentPlayer();
    const gameStatus = engine.getGameStatus();

    if (gameStatus.type === 'win') {
      if (gameMode === 'multiplayer-realtime') {
        return gameStatus.winner === playerColor ? '🎉 You Win!' : '😔 You Lost!';
      }
      const winnerName =
        gameStatus.winner === 'red'
          ? gameMode === 'local-multiplayer'
            ? 'Player 1'
            : 'Red'
          : gameMode === 'local-multiplayer'
          ? 'Player 2'
          : 'Yellow';
      return `${winnerName} Wins!`;
    }

    if (gameStatus.type === 'draw') return '🤝 Draw!';

    if (gameStatus.type === 'resignation') {
      if (gameMode === 'multiplayer-realtime') {
        return gameStatus.winner === playerColor ? '🎉 You Win!' : '😔 You Lost!';
      }
      const winnerName =
        gameStatus.winner === 'red'
          ? gameMode === 'local-multiplayer'
            ? 'Player 1'
            : 'Red'
          : gameMode === 'local-multiplayer'
          ? 'Player 2'
          : 'Yellow';
      return `${winnerName} Wins!`;
    }

    if (gameMode === 'multiplayer-realtime') {
      return currentPlayer === playerColor ? "Your Turn" : "Opponent's Turn";
    }

    if (gameMode === 'local-multiplayer') {
      return currentPlayer === 'red'
        ? "Player 1's Turn (Red)"
        : "Player 2's Turn (Yellow)";
    }

    if (gameMode === 'singleplayer') {
      return currentPlayer === 'red' ? 'Your Turn' : 'AI is thinking...';
    }

    return currentPlayer === 'red' ? "Red's Turn" : "Yellow's Turn";
  };

  // -----------------------------------------------------------------------
  // Interactivity
  // -----------------------------------------------------------------------

  const isGameOver = engine.isGameOver();

  const isBoardInteractive = (): boolean => {
    if (isGameOver) return false;

    if (gameMode === 'singleplayer') {
      return engine.getCurrentPlayer() === 'red' && !isAIThinking;
    }

    if (gameMode === 'multiplayer-realtime') {
      if (!playerColor) return false;
      return multiplayerService.isMyTurn(engine.getCurrentPlayer());
    }

    return true;
  };

  // -----------------------------------------------------------------------
  // Render: Mode selector
  // -----------------------------------------------------------------------

  if (showModeSelector) {
    const connectFourConfig: GameModeConfig = {
      gameName: 'Connect Four',
      gameIcon: '🔴',
      availableModes: ['ai', 'local', 'online'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '☀️', description: 'AI makes mistakes' },
        { id: 'medium', label: 'Medium', icon: '⭐', description: 'Balanced gameplay' },
        { id: 'hard', label: 'Hard', icon: '🏆', description: 'Unbeatable AI' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai' && selection.difficulty) {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as ConnectFourDifficulty,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({ mode: 'local-multiplayer' });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', isPrivate: true });
      }
    };

    return (
      <div className="cf-game-container">
        <UniversalGameModeSelector
          config={connectFourConfig}
          onStart={handleModeSelection}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime', isPrivate: true })}
          onJoinRoom={handleJoinPrivateGame}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={
            isAuthenticated
              ? undefined
              : GameSessionManager.getInstance().getRemainingFreeGames()
          }
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Waiting for opponent
  // -----------------------------------------------------------------------

  if (waitingForOpponent && roomCode) {
    return (
      <div className="cf-game-container">
        <RoomCodeDisplay
          roomCode={roomCode}
          gameType="connect-four"
          playerRole={
            playerColor === 'red' ? 'Red' : playerColor === 'yellow' ? 'Yellow' : undefined
          }
          playerName="Guest"
          maxPlayers={2}
          onCancel={handleCancelWaiting}
          showCancelButton={true}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Build UniversalGameLayout config
  // -----------------------------------------------------------------------

  const gameState = engine.getGameState();
  const canUndo =
    gameMode === 'singleplayer' &&
    !isAIThinking &&
    gameState.moveHistory.length > 0 &&
    !isGameOver;
  const isMultiplayer = gameMode === 'multiplayer-realtime';
  const isActiveGame = gameState.gameStatus.type === 'active';

  // Determine game-over state for overlay
  const gameStatus = engine.getGameStatus();
  const getGameOverConfig = () => {
    if (!isGameOver) return undefined;

    let result: 'win' | 'loss' | 'draw' = 'draw';
    let title = '🤝 Draw!';
    let subtitle: string | undefined;
    let icon = '🤝';

    if (gameStatus.type === 'win' || gameStatus.type === 'resignation') {
      if (gameMode === 'multiplayer-realtime') {
        const isWin = gameStatus.winner === playerColor;
        result = isWin ? 'win' : 'loss';
        title = isWin ? '🎉 You Win!' : '😔 You Lost!';
        icon = isWin ? '🏆' : '😔';
        subtitle = gameStatus.type === 'resignation' ? 'By resignation' : 'Four in a row!';
      } else {
        result = 'win';
        const winnerName =
          gameStatus.winner === 'red'
            ? gameMode === 'local-multiplayer' ? 'Player 1' : 'Red'
            : gameMode === 'local-multiplayer' ? 'Player 2' : 'Yellow';
        title = `${winnerName} Wins!`;
        icon = '🏆';
        subtitle = gameStatus.type === 'resignation' ? 'By resignation' : 'Four in a row!';
      }
    }

    return {
      show: true,
      result,
      title,
      subtitle,
      icon,
      onPlayAgain: handleNewGame,
      onExit: onExit || handleNewGame,
    };
  };

  // Left panels: Move History
  const leftPanels = [
    {
      id: 'history',
      title: 'Move History',
      icon: '📜',
      content: (
        <MoveHistory
          moves={gameState.moveHistory}
          currentMoveIndex={gameState.moveHistory.length - 1}
        />
      ),
    },
  ];

  // Right panels: Coach (single-player only)
  const rightPanels = gameMode === 'singleplayer' ? [
    {
      id: 'coach',
      title: 'AI Coach',
      icon: '🤖',
      content: (
        <GameCoach
          coach={coachRef.current}
          gameContext={{
            gameName: 'Connect Four',
            gameState: `Move ${gameState.moveHistory.length}. ${isGameOver ? 'Game over.' : 'Game in progress.'}`,
            playerContext: `Playing as Red. ${engine.getCurrentPlayer() === 'red' && !isAIThinking ? "It's your turn." : "Waiting for AI."}`,
          }}
          isPlayerTurn={engine.getCurrentPlayer() === 'red' && !isAIThinking}
          quickQuestions={['Best column?', 'Block or attack?', 'Double threat?', 'Center control?']}
          accentColor="#dc2626"
        />
      ),
    },
  ] : [];

  // Status bar
  const statusBar = {
    turnIndicator: (
      <span>
        {engine.getCurrentPlayer() === 'red' ? '🔴' : '🟡'}{' '}
        {getTurnIndicatorText()}
      </span>
    ),
    connectionStatus: isMultiplayer ? connectionStatus : undefined,
    roomCode: roomCode || undefined,
    gameStatus: isGameOver
      ? getTurnIndicatorText()
      : undefined,
    gameStatusType: isGameOver
      ? 'ended' as const
      : undefined,
    aiThinking: isAIThinking,
  };

  // Actions
  const actions = [
    ...(canUndo ? [{
      id: 'undo',
      label: 'Undo',
      icon: '↩️',
      onClick: handleUndo,
      variant: 'secondary' as const,
    }] : []),
    {
      id: 'new-game',
      label: 'New Game',
      icon: '🔄',
      onClick: handleNewGame,
      variant: 'primary' as const,
    },
    ...(isMultiplayer ? [{
      id: 'resign',
      label: 'Resign',
      icon: '🏳️',
      onClick: handleResign,
      disabled: !isActiveGame,
      variant: 'danger' as const,
    }] : []),
    ...(onExit ? [{
      id: 'exit',
      label: 'Exit',
      icon: '🚪',
      onClick: onExit,
      variant: 'secondary' as const,
    }] : []),
  ];

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Connect Four',
    board: (
      <ConnectFourBoard
        engine={engine}
        interactive={isBoardInteractive()}
        isAIThinking={isAIThinking}
        onMove={handleMove}
        moveCount={moveCount}
        playerColor={playerColor || undefined}
      />
    ),
    leftPanels,
    rightPanels,
    statusBar,
    actions,
    gameOver: getGameOverConfig(),
    onShare: isMultiplayer && roomCode ? () => {} : undefined,
    roomCode: roomCode || undefined,
    isLoading: false,
  };

  return (
    <div className="cf-game-container">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/**
 * ConnectFourGame wrapped in ErrorBoundary.
 */
export const ConnectFourGame: React.FC<ConnectFourGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="ConnectFour">
      <ConnectFourGameInner {...props} />
    </ErrorBoundary>
  );
};

export default ConnectFourGame;
